import React, { useState } from 'react';
import { Users, Gift, Copy, CheckCircle, Trophy, Star } from 'lucide-react';

const ReferralTracker = () => {
  const [email, setEmail] = useState('');
  const [isTracking, setIsTracking] = useState(false);
  const [copied, setCopied] = useState(false);

  // Demo data - in production this would come from the backend
  const [referralData, setReferralData] = useState(null);

  const handleTrack = (e) => {
    e.preventDefault();
    // Demo: simulate fetching referral data
    setReferralData({
      referralCode: 'ABBA-' + email.split('@')[0].toUpperCase().slice(0, 4) + Math.floor(Math.random() * 1000),
      totalReferrals: 0,
      pendingReferrals: 0,
      completedReferrals: 0,
      freeSessionsEarned: 0,
      freeSessionsUsed: 0
    });
    setIsTracking(true);
  };

  const copyReferralCode = () => {
    if (referralData?.referralCode) {
      navigator.clipboard.writeText(referralData.referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const rewards = [
    { referrals: 1, reward: '1 Free Session', icon: <Gift size={20} /> },
    { referrals: 3, reward: '3 Free Sessions + Priority Booking', icon: <Star size={20} /> },
    { referrals: 5, reward: '5 Free Sessions + 10% Off Forever', icon: <Trophy size={20} /> }
  ];

  return (
    <section style={{
      padding: '5rem 1.5rem',
      background: 'linear-gradient(135deg, #FFFCF5 0%, #E5C575 100%)'
    }}>
      <div className="container" style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'rgba(245, 158, 11, 0.2)',
            padding: '0.5rem 1rem',
            borderRadius: '9999px',
            marginBottom: '1rem'
          }}>
            <Users size={18} color="#0F172A" />
            <span style={{ color: '#0F172A', fontWeight: 600, fontSize: '0.9rem' }}>Referral Programme</span>
          </div>
          <h2 style={{ 
            fontSize: 'clamp(1.75rem, 4vw, 2.25rem)', 
            fontWeight: 800, 
            color: '#0F172A',
            marginBottom: '1rem'
          }}>
            Refer Friends, Earn Free Sessions
          </h2>
          <p style={{ color: '#78350F', fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto' }}>
            Share the gift of better grades! For every friend who joins, you both get rewarded.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '2rem'
        }}>
          {/* Left: Rewards tiers */}
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '2rem',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
          }}>
            <h3 style={{ 
              fontSize: '1.25rem', 
              fontWeight: 700, 
              color: '#0F172A', 
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Trophy size={24} color="#C5A059" />
              Reward Tiers
            </h3>

            {rewards.map((tier, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '1rem',
                  background: index === 2 ? 'linear-gradient(135deg, #FFFCF5 0%, #E5C575 100%)' : '#F8FAFC',
                  borderRadius: '12px',
                  marginBottom: index < 2 ? '1rem' : 0,
                  border: index === 2 ? '2px solid #C5A059' : '1px solid #E2E8F0'
                }}
              >
                <div style={{
                  width: '45px',
                  height: '45px',
                  borderRadius: '50%',
                  background: index === 2 ? '#C5A059' : '#E2E8F0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: index === 2 ? 'white' : '#64748B'
                }}>
                  {tier.icon}
                </div>
                <div>
                  <p style={{ margin: 0, fontWeight: 700, color: '#0F172A', fontSize: '0.95rem' }}>
                    {tier.referrals} Referral{tier.referrals > 1 ? 's' : ''}
                  </p>
                  <p style={{ margin: 0, color: '#64748B', fontSize: '0.85rem' }}>
                    {tier.reward}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Right: Track referrals */}
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '2rem',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
          }}>
            {!isTracking ? (
              <>
                <h3 style={{ 
                  fontSize: '1.25rem', 
                  fontWeight: 700, 
                  color: '#0F172A', 
                  marginBottom: '1rem' 
                }}>
                  Get Your Referral Code
                </h3>
                <p style={{ color: '#64748B', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
                  Enter your email to get your unique referral code and start earning rewards.
                </p>

                <form onSubmit={handleTrack}>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '1rem',
                      borderRadius: '12px',
                      border: '2px solid #E2E8F0',
                      fontSize: '1rem',
                      marginBottom: '1rem',
                      outline: 'none'
                    }}
                    data-testid="referral-email"
                  />
                  <button
                    type="submit"
                    style={{
                      width: '100%',
                      padding: '1rem',
                      borderRadius: '12px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #C5A059 0%, #D97706 100%)',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '1rem',
                      cursor: 'pointer'
                    }}
                    data-testid="referral-submit"
                  >
                    Get My Code
                  </button>
                </form>
              </>
            ) : (
              <>
                <h3 style={{ 
                  fontSize: '1.25rem', 
                  fontWeight: 700, 
                  color: '#0F172A', 
                  marginBottom: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <CheckCircle size={24} color="#C5A059" />
                  Your Referral Code
                </h3>

                {/* Referral code box */}
                <div style={{
                  background: '#FFFCF5',
                  borderRadius: '12px',
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  border: '2px dashed #C5A059',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span style={{ 
                    fontWeight: 800, 
                    fontSize: '1.25rem', 
                    color: '#0F172A',
                    letterSpacing: '1px'
                  }}>
                    {referralData?.referralCode}
                  </span>
                  <button
                    onClick={copyReferralCode}
                    style={{
                      background: copied ? '#C5A059' : '#C5A059',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.5rem 1rem',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                    data-testid="copy-code"
                  >
                    {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>

                {/* Stats */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{
                    background: '#F8FAFC',
                    borderRadius: '12px',
                    padding: '1rem',
                    textAlign: 'center'
                  }}>
                    <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: '#C5A059' }}>
                      {referralData?.totalReferrals || 0}
                    </p>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B' }}>
                      Total Referrals
                    </p>
                  </div>
                  <div style={{
                    background: '#F8FAFC',
                    borderRadius: '12px',
                    padding: '1rem',
                    textAlign: 'center'
                  }}>
                    <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: '#C5A059' }}>
                      {referralData?.freeSessionsEarned || 0}
                    </p>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B' }}>
                      Free Sessions Earned
                    </p>
                  </div>
                </div>

                <p style={{ 
                  color: '#64748B', 
                  fontSize: '0.85rem', 
                  textAlign: 'center',
                  margin: 0
                }}>
                  Share your code with friends. When they sign up, you both get rewarded! 🎉
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReferralTracker;
